import { useContext, useEffect, useState } from 'react'
import { usePrevious } from 'react-use'
import CreationToolCrop from '../../../types/crop'
import CreationToolSourceEditor from '../../../types/source-editor'
import CropLayer from '../../artboard/layer/crop'
import { CreationSourceEditorContext } from '../manager'

type Props = {
    canvasScale?: number
}

const CreationToolsSourceEditorArtboardCrop = ({ canvasScale = 1 }: Props) => {
    const { edits, source, currentEditor, cropType, crop, setCrop } = useContext(CreationSourceEditorContext)
    const previousCropType = usePrevious(cropType)
    const [defaultWidth, setDefaultWidth] = useState<number>(crop.width)
    const [defaultHeight, setDefaultHeight] = useState<number>(crop.height)
    const [defaultX, setDefaultX] = useState<number>(crop.x + crop.width / 2)
    const [defaultY, setDefaultY] = useState<number>(crop.y + crop.height / 2)
    const isCustomCrop = cropType === CreationToolCrop.Custom
    const isCropEditorActive = currentEditor === CreationToolSourceEditor.Crop

    useEffect(() => {
        if (!previousCropType || cropType === previousCropType) return

        const aspectRatio = CreationToolCrop.aspectRatio(cropType)

        // reset crop size/position when changing crop type
        setDefaultX(source.width / 2)
        setDefaultY(source.height / 2)

        if (aspectRatio) {
            let width = source.width
            let height = width / aspectRatio

            if (height > source.height) {
                height = source.height
                width = height * aspectRatio
            }

            setDefaultWidth(width)
            setDefaultHeight(height)
        } else {
            setDefaultWidth(source.width)
            setDefaultHeight(source.height)
        }
    }, [source, cropType, previousCropType, setDefaultWidth, setDefaultHeight, setDefaultX, setDefaultY])

    return edits?.crop || isCropEditorActive ? (
        <CropLayer
            {...{
                defaultX: defaultX / canvasScale,
                defaultY: defaultY / canvasScale,
                key: `crop-${defaultWidth}x${defaultHeight}`, // we want to re-render when crop type changes so sizing resets
                aspectRatio: isCustomCrop ? undefined : defaultWidth / defaultHeight,
                width: defaultWidth / canvasScale,
                height: defaultHeight / canvasScale,
                moveable: true,
                resizeable: true,
                editable: isCropEditorActive,
                onUpdate: setCrop,
            }}
        />
    ) : null
}

export default CreationToolsSourceEditorArtboardCrop
